/**
 * What to call ourselves in text a user reads.
 *
 * CLN-6.10. The rule is CLN-6.6's, and `noSolerroFallback.test.ts` already enforces half
 * of it: outside the Solerro account, nothing falls back to Solerro. This module supplies
 * the other half — the thing to say INSTEAD — so that the answer is written once rather
 * than inlined as a literal at every call site. `HomePage.tsx` had the right expression
 * (`currentTenant?.company_name?.trim() || 'Joules'`) and it was the only place that did.
 *
 * ⚠️ NOT `platformTenant.ts`. That module is an owner id for shared content and says in
 * its own header never to use it as a stand-in for the current account. This is a display
 * name and nothing else; it resolves no ids and reads no rows.
 *
 * ⚠️ ON UNAUTHENTICATED PAGES THE ACCOUNT IS GENUINELY UNKNOWN, so they get the platform
 * name rather than a guess. `SignContractPage` and `BookingConfirmationPage` run with no
 * session, which means `get_current_user_tenant_id()` is NULL and no account name is
 * readable — `SignContractPage` already carries a note about exactly this. Saying "Joules"
 * there is honest; naming one customer's company to another customer's signer is not.
 */

/** The product's name. The iOS app ships as "Joules CRM" (CLN-6.4); this is the short form. */
export const PLATFORM_BRAND = 'Joules';

/**
 * The account's own name where we know it, the platform's name where we do not.
 *
 * Accepts the raw `tenants.company_name`, including the nulls and whitespace-only values
 * that are actually in that column, so callers do not each re-implement the trim.
 */
export function brandName(tenantCompanyName?: string | null): string {
  const named = tenantCompanyName?.trim();
  return named ? named : PLATFORM_BRAND;
}
